'use client';
import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import StudentVerificationBadge from '@/components/ui/StudentVerificationBadge';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import PhoneInput from '@/components/ui/PhoneInput';
import { CountryPhone } from '@/lib/phoneUtils';
import TwoFactorChallengeModal from '@/components/TwoFactorChallengeModal';
import { sendSignupConfirmationEmail } from '@/lib/emailService';

type AuthTab = 'login' | 'signup';
type UserRole = 'sender' | 'customer' | 'traveler' | 'host' | 'student' | 'courier';

interface LoginFormData {
  emailOrUsername: string;
  password: string;
  remember: boolean;
}

interface SignupFormData {
  firstName: string;
  middleName: string;
  surname: string;
  username: string;
  email: string;
  phone: string;
  country: string;
  currency: string;
  role: UserRole;
  gender: string;
  dateOfBirth: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
  isStudent: boolean;
  studentId: string;
  schoolName: string;
  schoolType: string;
  yearOfStudy: string;
  courseProgram: string;
  guarantorRelationship: string;
  guarantorFullName: string;
  guarantorPhone: string;
  guarantorIdNumber: string;
}

const roles: { id: UserRole; label: string; icon: string; desc: string }[] = [
  { id: 'customer', label: 'Customer', icon: 'ArchiveBoxIcon', desc: 'Send parcels and book storage with verified travelers' },
  { id: 'traveler', label: 'Traveler', icon: 'TruckIcon', desc: 'Earn from your unused luggage space' },
  { id: 'student', label: 'Student Traveler', icon: 'AcademicCapIcon', desc: 'Earn while travelling home or between schools' },
  { id: 'host', label: 'Storage Host', icon: 'BuildingStorefrontIcon', desc: 'Offer secure temporary storage' },
  { id: 'courier', label: 'Courier', icon: 'TruckIcon', desc: 'Professional courier — carry up to 5 tonnes, requires admin approval' },
];

const schoolTypes = ['University', 'College / Polytechnic', 'Secondary School', 'Primary School', 'Other'];
const guarantorRelationships = ['Parent', 'Guardian', 'Close Family Member', 'Teacher / Lecturer', 'School Administrator'];
const yearsOfStudy = ['Year 1', 'Year 2', 'Year 3', 'Year 4', 'Year 5', 'Year 6', 'Form 1', 'Form 2', 'Form 3', 'Form 4', 'Other'];

const ALLOWED_EMAIL_DOMAINS = ['gmail.com', 'yahoo.com', 'rocketmail.com', 'outlook.com'];

const roleHomeMap: Record<string, string> = {
  sender: '/sender-home',
  customer: '/sender-home',
  traveler: '/traveler-home',
  student: '/traveler-home',
  host: '/host-home',
  admin: '/admin-dashboard',
  station_moderator: '/station-moderator',
  courier: '/courier-dashboard',
};

function validateEmailDomain(email: string): string | true {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return 'Invalid email address';
  if (!ALLOWED_EMAIL_DOMAINS.includes(domain)) {
    return `Only ${ALLOWED_EMAIL_DOMAINS.join(', ')} email addresses are accepted`;
  }
  return true;
}

function validateAge(dob: string): string | true {
  if (!dob) return 'Date of birth is required';
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  if (age < 18) return 'You must be at least 18 years old to register';
  return true;
}

export default function AuthScreen() {
  const [tab, setTab] = useState<AuthTab>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState<UserRole>('customer');
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isStudent, setIsStudent] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState<CountryPhone | null>(null);
  const [guarantorPhoneCountry, setGuarantorPhoneCountry] = useState<CountryPhone | null>(null);
  const [show2FAChallenge, setShow2FAChallenge] = useState(false);
  const [pending2FAUserId, setPending2FAUserId] = useState('');
  const [pending2FADestination, setPending2FADestination] = useState('');
  const [pending2FAToastMsg, setPending2FAToastMsg] = useState('');

  // Real-time field validation state
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneChecking, setPhoneChecking] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  const { signIn, signUp } = useAuth();
  const router = useRouter();

  const loginForm = useForm<LoginFormData>({ defaultValues: { emailOrUsername: '', password: '', remember: false } });
  const signupForm = useForm<SignupFormData>({ defaultValues: { role: 'customer', country: 'KE', currency: 'KES', isStudent: false, gender: '' } });

  const handleCheckUsername = async (value: string) => {
    if (!value || value.length < 3) return;
    setUsernameChecking(true);
    setUsernameError('');
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('username', value.trim())
        .maybeSingle();
      if (data) {
        setUsernameError('This username is already taken. Please choose a different one.');
        signupForm.setError('username', { message: 'Username already taken.' });
      } else {
        signupForm.clearErrors('username');
      }
    } catch {
      // silent
    } finally {
      setUsernameChecking(false);
    }
  };

  const handleCheckEmail = async (value: string) => {
    if (!value || !value.includes('@')) return;
    const domainCheck = validateEmailDomain(value);
    if (domainCheck !== true) { setEmailError(domainCheck); return; }
    setEmailChecking(true);
    setEmailError('');
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('email', value.trim().toLowerCase())
        .maybeSingle();
      if (data) {
        setEmailError('An account with this email already exists. Please use a different email or sign in.');
        signupForm.setError('email', { message: 'Email already registered.' });
      } else {
        signupForm.clearErrors('email');
      }
    } catch {
      // silent
    } finally {
      setEmailChecking(false);
    }
  };

  const handleCheckPhone = async (value: string) => {
    if (!value || value.length < 8) return;
    setPhoneChecking(true);
    setPhoneError('');
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('phone', value.trim())
        .maybeSingle();
      if (data) {
        setPhoneError('This phone number is already registered. Please use a different number.');
        signupForm.setError('phone', { message: 'Phone already registered.' });
      } else {
        signupForm.clearErrors('phone');
      }
    } catch {
      // silent
    } finally {
      setPhoneChecking(false);
    }
  };

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    setLoginError('');
    try {
      const supabase = createClient();
      let emailToUse = data.emailOrUsername.trim();

      // If input doesn't look like an email, treat it as a username and look up the email
      if (!emailToUse.includes('@')) {
        const { data: profileData, error: profileError } = await supabase
          .from('user_profiles')
          .select('email')
          .eq('username', emailToUse)
          .maybeSingle();

        if (profileError || !profileData?.email) {
          setLoginError('No account found with that username. Please check and try again.');
          setIsLoading(false);
          return;
        }
        emailToUse = profileData.email;
      }

      await signIn(emailToUse, data.password);

      // Fetch user profile to get role for redirect
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('role, totp_enabled')
          .eq('id', user.id)
          .single();

        const role = profile?.role || 'customer';
        const destination = roleHomeMap[role] || '/sender-home';

        // Check if 2FA is required
        if (profile?.totp_enabled) {
          setPending2FAUserId(user.id);
          setPending2FADestination(destination);
          setPending2FAToastMsg(role === 'admin' ? 'Welcome back, Admin!' : 'Welcome back to MuyuRide!');
          setShow2FAChallenge(true);
          setIsLoading(false);
          return;
        }

        toast.success(role === 'admin' ? 'Welcome back, Admin!' : 'Welcome back to MuyuRide!');
        router.push(destination);
      } else {
        toast.success('Welcome back to MuyuRide!');
        router.push('/sender-home');
      }
    } catch (err: any) {
      setLoginError(err?.message || 'Invalid credentials. Please check your email/username and password.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (data: SignupFormData) => {
    // Block if any duplicate field errors exist
    if (usernameError || emailError || phoneError) {
      toast.error('Please fix the highlighted errors before continuing.');
      return;
    }
    setIsLoading(true);
    try {
      // Validate username uniqueness
      const supabase = createClient();
      if (data.username) {
        const { data: existingUser } = await supabase
          .from('user_profiles')
          .select('id')
          .eq('username', data.username.trim())
          .maybeSingle();
        if (existingUser) {
          signupForm.setError('username', { message: 'Username already taken. Please choose another.' });
          setIsLoading(false);
          return;
        }
      }

      const fullName = [data.firstName, data.middleName, data.surname].filter(Boolean).join(' ');
      // Students register as traveler role in the system; couriers keep their role
      const effectiveRole = selectedRole === 'student' ? 'traveler' : (selectedRole === 'customer' ? 'customer' : selectedRole);
      const effectiveIsStudent = selectedRole === 'student' || isStudent;
      await signUp(data.email, data.password, {
        fullName,
        role: effectiveRole,
        phone: data.phone,
        username: data.username,
        gender: data.gender || '',
        dateOfBirth: data.dateOfBirth || '',
        ...(effectiveIsStudent && (selectedRole === 'traveler' || selectedRole === 'student') ? {
          isStudent: true,
          studentId: data.studentId,
          schoolName: data.schoolName,
          schoolType: data.schoolType,
          yearOfStudy: data.yearOfStudy,
          courseProgram: data.courseProgram,
          guarantorRelationship: data.guarantorRelationship,
          guarantorFullName: data.guarantorFullName,
          guarantorPhone: data.guarantorPhone,
          guarantorIdNumber: data.guarantorIdNumber,
        } : {}),
      });

      // Send signup confirmation email (fire-and-forget)
      sendSignupConfirmationEmail({
        email: data.email,
        name: fullName || data.firstName || 'there',
        role: effectiveRole,
      }).catch(() => {});

      if (effectiveIsStudent) {
        toast.success('Student traveler account created! Your student ID and school info will be reviewed for verification.');
      } else if (selectedRole === 'courier') {
        toast.success('Courier account created! Please sign in and complete your courier registration to apply.');
      } else {
        toast.success('Account created! Please check your email to verify, then sign in.');
      }
      setTab('login');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      toast.error('Please enter your email address.');
      return;
    }
    setForgotLoading(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/auth/callback?next=/profile`,
      });
      if (error) throw error;
      setForgotSent(true);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handle2FASuccess = () => {
    setShow2FAChallenge(false);
    toast.success(pending2FAToastMsg || 'Welcome back to MuyuRide!');
    router.push(pending2FADestination || '/sender-home');
  };

  const handle2FACancel = async () => {
    setShow2FAChallenge(false);
    const supabase = createClient();
    await supabase.auth.signOut();
    setPending2FAUserId('');
    setPending2FADestination('');
  };

  const platformStats = [
    { value: '4,820+', label: 'Verified Travelers' },
    { value: '31,400+', label: 'Deliveries' },
    { value: '280+', label: 'Routes' },
    { value: '4.87★', label: 'Avg Rating' },
  ];

  return (
    <>
      <TwoFactorChallengeModal
        isOpen={show2FAChallenge}
        userId={pending2FAUserId}
        onSuccess={handle2FASuccess}
        onCancel={handle2FACancel}
      />
      <div className="min-h-screen flex">
        {/* Left brand panel */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] gradient-primary flex-col justify-between p-10 relative overflow-hidden">
          <div className="blob-teal absolute inset-0 pointer-events-none" />

          {/* Logo */}
          <div className="flex items-center gap-3 relative z-10">
            <AppLogo size={40} variant="white" />
          </div>

          {/* Hero copy */}
          <div className="relative z-10 space-y-6">
            <div>
              <h1 className="text-hero text-white leading-tight">
                Every Traveler is a Courier.<br />
                Every Route is a Delivery.
              </h1>
              <p className="mt-4 text-white/80 text-base leading-relaxed max-w-md">
                Kenya&apos;s most trusted peer-to-peer travel marketplace. Send parcels safely with verified travelers across all 47 counties.
              </p>
            </div>

            {/* Transport icons */}
            <div className="flex flex-wrap gap-3">
              {['Bus', 'Matatu', 'Train', 'Flight', 'Motorcycle', 'Boat'].map((mode) => (
                <span key={`mode-${mode}`} className="px-3 py-1.5 bg-white/20 text-white text-xs font-500 rounded-full border border-white/30">
                  {mode}
                </span>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              {platformStats.map((s) => (
                <div key={`pstat-${s.label}`} className="bg-white/15 rounded-xl p-4 border border-white/20">
                  <p className="text-2xl font-700 text-white tabular-nums">{s.value}</p>
                  <p className="text-white/70 text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Student program highlight */}
            <div className="bg-white/10 border border-white/20 rounded-xl p-3 flex items-start gap-3">
              <Icon name="AcademicCapIcon" size={20} className="text-white/80 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-xs font-600">Student Traveler Program</p>
                <p className="text-white/70 text-[11px] mt-0.5 leading-relaxed">Students travelling home or between schools can earn by carrying verified parcels. Special student verification badge included.</p>
              </div>
            </div>
          </div>

          {/* Trust badge */}
          <div className="relative z-10 flex items-center gap-2 text-white/70 text-xs">
            <Icon name="ShieldCheckIcon" size={16} className="text-white/60" />
            <span>Verified identities · Escrow protection · GPS tracking · Real-time support</span>
          </div>
        </div>

        {/* Right form panel */}
        <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-12 xl:px-16 py-10 overflow-y-auto">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <AppLogo size={32} />
          </div>

          {/* Tab switcher */}
          <div className="flex bg-muted rounded-xl p-1 mb-8 max-w-sm">
            {(['login', 'signup'] as AuthTab[]).map((t) => (
              <button
                key={`tab-${t}`}
                onClick={() => setTab(t)}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-600 transition-all duration-200
                  ${tab === t ? 'bg-card shadow-card text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {t === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {tab === 'login' ? (
            /* ——— LOGIN FORM ——— */
            <div className="max-w-sm w-full space-y-5">
              <div>
                <h2 className="text-2xl font-700 text-foreground">Welcome back</h2>
                <p className="text-muted-foreground text-sm mt-1">Sign in with your email or username</p>
              </div>

              {loginError && (
                <div className="flex items-start gap-2 p-3 bg-danger/5 border border-danger/20 rounded-lg">
                  <Icon name="ExclamationCircleIcon" size={16} className="text-danger mt-0.5 flex-shrink-0" />
                  <p className="text-danger text-xs font-500">{loginError}</p>
                </div>
              )}

              {showForgotPassword ? (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-700 text-foreground">Reset your password</h3>
                    <p className="text-muted-foreground text-sm mt-1">Enter your email and we&apos;ll send a reset link.</p>
                  </div>
                  {forgotSent ? (
                    <div className="flex items-start gap-3 p-4 bg-success/5 border border-success/20 rounded-xl">
                      <Icon name="CheckCircleIcon" size={18} className="text-success flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-600 text-success">Reset email sent!</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Check your inbox at <strong>{forgotEmail}</strong> and follow the link to reset your password.</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-500 text-foreground mb-1.5">Email address</label>
                        <input
                          type="email"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                          placeholder="you@gmail.com"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        disabled={forgotLoading}
                        className="w-full flex items-center justify-center gap-2 py-3 gradient-primary text-primary-foreground rounded-lg font-600 text-sm hover:opacity-90 transition-all duration-150 active:scale-95 disabled:opacity-60"
                      >
                        {forgotLoading ? (
                          <><Icon name="ArrowPathIcon" size={16} className="animate-spin" /><span>Sending…</span></>
                        ) : (
                          <><Icon name="EnvelopeIcon" size={16} /><span>Send Reset Link</span></>
                        )}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(false); setForgotSent(false); setForgotEmail(''); }}
                    className="w-full text-sm text-primary font-500 hover:underline text-center"
                  >
                    ← Back to Sign In
                  </button>
                </div>
              ) : (
              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">Email or Username</label>
                  <input
                    type="text"
                    {...loginForm.register('emailOrUsername', { required: 'Email or username is required' })}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                    placeholder="you@gmail.com or your_username"
                    autoComplete="username"
                  />
                  {loginForm.formState.errors.emailOrUsername && (
                    <p className="mt-1 text-xs text-danger">{loginForm.formState.errors.emailOrUsername.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-500 text-foreground mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      {...loginForm.register('password', { required: 'Password is required' })}
                      className="w-full px-3.5 py-2.5 pr-10 rounded-lg border border-input bg-card text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="mt-1 text-xs text-danger">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...loginForm.register('remember')} className="rounded border-input text-primary focus:ring-ring" />
                    <span className="text-sm text-muted-foreground">Remember me</span>
                  </label>
                  <button type="button" onClick={() => setShowForgotPassword(true)} className="text-sm text-primary font-500 hover:underline">Forgot password?</button>
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 gradient-primary text-primary-foreground rounded-lg font-600 text-sm hover:opacity-90 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ minWidth: '100%' }}
                >
                  {isLoading ? (
                    <>
                      <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                      <span>Signing in…</span>
                    </>
                  ) : (
                    <>
                      <Icon name="ArrowRightCircleIcon" size={16} />
                      <span>Sign In</span>
                    </>
                  )}
                </button>
              </form>
              )}

              <p className="text-center text-sm text-muted-foreground">
                No account?{' '}
                <button onClick={() => setTab('signup')} className="text-primary font-600 hover:underline">
                  Create one free
                </button>
              </p>
            </div>
          ) : (
            /* ——— SIGNUP FORM ——— */
            <div className="max-w-lg w-full space-y-5">
              <div>
                <h2 className="text-2xl font-700 text-foreground">Create your account</h2>
                <p className="text-muted-foreground text-sm mt-1">Join Kenya&apos;s most trusted travel marketplace</p>
              </div>

              {/* Role selector */}
              <div>
                <label className="block text-sm font-500 text-foreground mb-2">I want to join as</label>
                <div className="grid grid-cols-2 gap-2">
                  {roles.map((r) => (
                    <button
                      key={`role-${r.id}`}
                      type="button"
                      onClick={() => {
                        setSelectedRole(r.id);
                        signupForm.setValue('role', r.id as any);
                        if (r.id === 'student') {
                          setIsStudent(true);
                        } else if (r.id !== 'traveler') {
                          setIsStudent(false);
                        }
                      }}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 text-center transition-all duration-150
                        ${selectedRole === r.id
                          ? r.id === 'student' ?'border-violet-500 bg-violet-50 text-violet-700' :'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-primary/40 hover:bg-muted/50'
                        }`}
                    >
                      <Icon name={r.icon as Parameters<typeof Icon>[0]['name']} size={22} className={
                        selectedRole === r.id
                          ? r.id === 'student' ? 'text-violet-600' : 'text-primary'
                          : 'text-muted-foreground'
                      } />
                      <span className="text-xs font-600 leading-tight">{r.label}</span>
                      <span className="text-[10px] leading-tight opacity-70 hidden sm:block">{r.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Courier notice */}
              {selectedRole === 'courier' && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
                  <Icon name="InformationCircleIcon" size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-blue-800">
                    <p className="font-semibold">Courier Registration requires admin approval</p>
                    <p className="mt-0.5 opacity-80">After signing up, you will need to upload your National ID, Driving License, Police Clearance Certificate, CV, and Application Letter. You must also add your transport vessel details and sign a commitment agreement before your application is reviewed.</p>
                  </div>
                </div>
              )}

              <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4">
                {/* Name row */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-500 text-foreground mb-1">First name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      {...signupForm.register('firstName', { required: 'Required' })}
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="Amina"
                    />
                    {signupForm.formState.errors.firstName && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.firstName.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-foreground mb-1">Middle name</label>
                    <input
                      type="text"
                      {...signupForm.register('middleName')}
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="Njeri"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-foreground mb-1">Surname <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      {...signupForm.register('surname', { required: 'Required' })}
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="Hassan"
                    />
                    {signupForm.formState.errors.surname && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.surname.message}</p>}
                  </div>
                </div>

                {/* Username + Email */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-500 text-foreground mb-1">Username <span className="text-danger">*</span></label>
                    <p className="text-[10px] text-muted-foreground mb-1">Unique — used for login & profile</p>
                    <div className="relative">
                      <input
                        type="text"
                        {...signupForm.register('username', {
                          required: 'Required',
                          minLength: { value: 3, message: 'Min 3 chars' },
                          pattern: { value: /^[a-zA-Z0-9_]+$/, message: 'Letters, numbers, underscores only' }
                        })}
                        onBlur={(e) => handleCheckUsername(e.target.value)}
                        className={`w-full px-3 py-2.5 rounded-lg border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${usernameError ? 'border-danger' : 'border-input'}`}
                        placeholder="amina_njeri"
                      />
                      {usernameChecking && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">Checking…</span>}
                    </div>
                    {usernameError && <p className="mt-0.5 text-[10px] text-danger">{usernameError}</p>}
                    {!usernameError && signupForm.formState.errors.username && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.username.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-foreground mb-1">Email address <span className="text-danger">*</span></label>
                    <p className="text-[10px] text-muted-foreground mb-1">Gmail, Yahoo, Outlook, Rocketmail</p>
                    <div className="relative">
                      <input
                        type="email"
                        {...signupForm.register('email', {
                          required: 'Required',
                          pattern: { value: /^\S+@\S+\.\S+$/i, message: 'Invalid email' },
                          validate: validateEmailDomain,
                        })}
                        onBlur={(e) => handleCheckEmail(e.target.value)}
                        className={`w-full px-3 py-2.5 rounded-lg border bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring ${emailError ? 'border-danger' : 'border-input'}`}
                        placeholder="you@gmail.com"
                      />
                      {emailChecking && <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">Checking…</span>}
                    </div>
                    {emailError && <p className="mt-0.5 text-[10px] text-danger">{emailError}</p>}
                    {!emailError && signupForm.formState.errors.email && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.email.message}</p>}
                  </div>
                </div>

                {/* Gender + Date of Birth */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-500 text-foreground mb-1">Gender <span className="text-danger">*</span></label>
                    <select
                      {...signupForm.register('gender', { required: 'Required' })}
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="">Select gender…</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                      <option value="prefer_not_to_say">Prefer not to say</option>
                    </select>
                    {signupForm.formState.errors.gender && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.gender.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-foreground mb-1">Date of Birth <span className="text-danger">*</span></label>
                    <p className="text-[10px] text-muted-foreground mb-1">Must be 18 years or older</p>
                    <input
                      type="date"
                      {...signupForm.register('dateOfBirth', {
                        required: 'Required',
                        validate: validateAge,
                      })}
                      max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                      className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    {signupForm.formState.errors.dateOfBirth && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.dateOfBirth.message}</p>}
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <p className="text-[10px] text-muted-foreground mb-1">Used for SMS verification and delivery updates</p>
                  <PhoneInput
                    label="Phone number"
                    required
                    value={signupForm.watch('phone') || ''}
                    onChange={(full) => {
                      signupForm.setValue('phone', full, { shouldValidate: true });
                      setPhoneError('');
                    }}
                    onBlur={() => handleCheckPhone(signupForm.getValues('phone'))}
                    defaultCountry="KE"
                    error={phoneError || signupForm.formState.errors.phone?.message}
                  />
                  {phoneChecking && <p className="mt-0.5 text-[10px] text-muted-foreground">Checking availability…</p>}
                  {phoneError && <p className="mt-0.5 text-[10px] text-danger">{phoneError}</p>}
                </div>

                {/* Password */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-500 text-foreground mb-1">Password <span className="text-danger">*</span></label>
                    <p className="text-[10px] text-muted-foreground mb-1">Min 6 chars, upper, lower, number, symbol</p>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        {...signupForm.register('password', {
                          required: 'Required',
                          minLength: { value: 6, message: 'Min 6 characters' },
                          pattern: { value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/, message: 'Must include upper, lower, number, symbol' }
                        })}
                        className="w-full px-3 py-2.5 pr-9 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="••••••••"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                        <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={14} />
                      </button>
                    </div>
                    {signupForm.formState.errors.password && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.password.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-500 text-foreground mb-1">Confirm password <span className="text-danger">*</span></label>
                    <p className="text-[10px] text-muted-foreground mb-1">Re-enter your password</p>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        {...signupForm.register('confirmPassword', {
                          required: 'Required',
                          validate: (val) => val === signupForm.watch('password') || 'Passwords do not match'
                        })}
                        className="w-full px-3 py-2.5 pr-9 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="••••••••"
                      />
                      <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
                        <Icon name={showConfirmPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={14} />
                      </button>
                    </div>
                    {signupForm.formState.errors.confirmPassword && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.confirmPassword.message}</p>}
                  </div>
                </div>

                {/* Traveler verification notice */}
                {(selectedRole === 'traveler' || selectedRole === 'student') && (
                  <div className="bg-info/5 border border-info/20 rounded-xl p-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Icon name="ShieldCheckIcon" size={16} className="text-info" />
                      <p className="text-xs font-600 text-info">Traveler Verification Required</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      After signup you must complete: National ID / Passport upload, Selfie verification, Phone OTP verification, Emergency contact, and Guarantor details. No trips can be published until fully verified.
                    </p>
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {[
                        { icon: '🪪', label: 'ID Document' },
                        { icon: '📱', label: 'Phone OTP' },
                        { icon: '🤳', label: 'Selfie' },
                        { icon: '🛡️', label: 'Identity Badge' },
                      ].map(item => (
                        <span key={item.label} className="flex items-center gap-1 px-2 py-0.5 bg-info/10 text-info rounded-full text-[10px] font-500">
                          <span>{item.icon}</span>{item.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sender verification notice */}
                {selectedRole === 'sender' && (
                  <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Icon name="IdentificationIcon" size={16} className="text-primary" />
                      <p className="text-xs font-600 text-primary">Optional Identity Verification</p>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                      Verify your phone number and upload an ID document after signup to get a trusted sender badge. Verified senders get priority matching with top-rated travelers.
                    </p>
                  </div>
                )}

                {/* ——— STUDENT TRAVELER SECTION ——— */}
                {(selectedRole === 'traveler' || selectedRole === 'student') && (
                  <div className="border border-violet-200 rounded-xl overflow-hidden">
                    {selectedRole === 'traveler' ? (
                      <button
                        type="button"
                        onClick={() => setIsStudent(!isStudent)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-violet-50 hover:bg-violet-100 transition-colors"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon name="AcademicCapIcon" size={18} className="text-violet-600" />
                          <div className="text-left">
                            <p className="text-sm font-600 text-violet-800">I am a Student Traveler</p>
                            <p className="text-[11px] text-violet-600">Earn while travelling home or between schools</p>
                          </div>
                        </div>
                        <div className={`w-10 h-5 rounded-full transition-colors duration-200 flex items-center px-0.5 flex-shrink-0 ${isStudent ? 'bg-violet-600' : 'bg-gray-300'}`}>
                          <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${isStudent ? 'translate-x-5' : 'translate-x-0'}`} />
                        </div>
                      </button>
                    ) : (
                      <div className="flex items-center gap-2.5 px-4 py-3 bg-violet-600">
                        <Icon name="AcademicCapIcon" size={18} className="text-white" />
                        <div>
                          <p className="text-sm font-600 text-white">Student Traveler Program</p>
                          <p className="text-[11px] text-violet-200">Complete your student details below to get verified</p>
                        </div>
                      </div>
                    )}

                    {(isStudent || selectedRole === 'student') && (
                      <div className="p-4 space-y-4 bg-violet-50/30">
                        <div className="flex items-center gap-2">
                          <StudentVerificationBadge size="md" showLabel={true} />
                          <span className="text-[11px] text-muted-foreground">This badge will appear on your profile after verification</span>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-500 text-foreground mb-1">Student ID Number <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              {...signupForm.register('studentId', { required: isStudent ? 'Required for student travelers' : false })}
                              className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                              placeholder="e.g. UON/2024/001"
                            />
                            {signupForm.formState.errors.studentId && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.studentId.message}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-500 text-foreground mb-1">School / Institution Name <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              {...signupForm.register('schoolName', { required: isStudent ? 'Required' : false })}
                              className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                              placeholder="e.g. University of Nairobi"
                            />
                            {signupForm.formState.errors.schoolName && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.schoolName.message}</p>}
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-500 text-foreground mb-1">Institution Type <span className="text-danger">*</span></label>
                            <select
                              {...signupForm.register('schoolType', { required: isStudent ? 'Required' : false })}
                              className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                              <option value="">Select type…</option>
                              {schoolTypes.map(t => <option key={`stype-${t}`} value={t}>{t}</option>)}
                            </select>
                            {signupForm.formState.errors.schoolType && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.schoolType.message}</p>}
                          </div>
                          <div>
                            <label className="block text-xs font-500 text-foreground mb-1">Year / Form <span className="text-danger">*</span></label>
                            <select
                              {...signupForm.register('yearOfStudy', { required: isStudent ? 'Required' : false })}
                              className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                              <option value="">Select year…</option>
                              {yearsOfStudy.map(y => <option key={`year-${y}`} value={y}>{y}</option>)}
                            </select>
                            {signupForm.formState.errors.yearOfStudy && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.yearOfStudy.message}</p>}
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-500 text-foreground mb-1">Course / Programme</label>
                          <input
                            type="text"
                            {...signupForm.register('courseProgram')}
                            className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            placeholder="e.g. Bachelor of Commerce"
                          />
                        </div>

                        <div className="border-t border-violet-200 pt-3">
                          <div className="flex items-center gap-2 mb-3">
                            <Icon name="UserGroupIcon" size={15} className="text-violet-600" />
                            <p className="text-xs font-700 text-violet-800">Parent / Teacher / Guarantor Details</p>
                          </div>
                          <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
                            As a student traveler, your guarantor must be reachable during every journey. This person will be visible to senders before they accept your bid.
                          </p>

                          <div className="mb-3">
                            <label className="block text-xs font-500 text-foreground mb-1">Relationship to You <span className="text-danger">*</span></label>
                            <select
                              {...signupForm.register('guarantorRelationship', { required: isStudent ? 'Required' : false })}
                              className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                            >
                              <option value="">Select relationship…</option>
                              {guarantorRelationships.map(r => <option key={`rel-${r}`} value={r}>{r}</option>)}
                            </select>
                            {signupForm.formState.errors.guarantorRelationship && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.guarantorRelationship.message}</p>}
                          </div>

                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs font-500 text-foreground mb-1">Guarantor Full Name <span className="text-danger">*</span></label>
                              <input
                                type="text"
                                {...signupForm.register('guarantorFullName', { required: isStudent ? 'Required' : false })}
                                className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                placeholder="Full legal name"
                              />
                              {signupForm.formState.errors.guarantorFullName && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.guarantorFullName.message}</p>}
                            </div>
                            <div>
                              <label className="block text-xs font-500 text-foreground mb-1">Guarantor ID Number <span className="text-danger">*</span></label>
                              <input
                                type="text"
                                {...signupForm.register('guarantorIdNumber', { required: isStudent ? 'Required' : false })}
                                className="w-full px-3 py-2.5 rounded-lg border border-input bg-card text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                                placeholder="National ID / Passport"
                              />
                              {signupForm.formState.errors.guarantorIdNumber && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.guarantorIdNumber.message}</p>}
                            </div>
                          </div>

                          <div>
                            <PhoneInput
                              label="Guarantor Phone Number"
                              required={isStudent}
                              value={signupForm.watch('guarantorPhone') || ''}
                              onChange={(full) => signupForm.setValue('guarantorPhone', full, { shouldValidate: true })}
                              defaultCountry="KE"
                              error={signupForm.formState.errors.guarantorPhone?.message}
                            />
                          </div>
                        </div>

                        <div className="flex items-start gap-2.5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                          <Icon name="InformationCircleIcon" size={15} className="text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-600 text-amber-800">Student Parcel Limits Apply</p>
                            <p className="text-[11px] text-amber-700 mt-0.5 leading-relaxed">
                              New student travelers are limited to parcels up to <strong>5kg</strong> and a maximum declared value of <strong>KES 5,000</strong> per trip. Limits increase as you build your trust score and complete verified deliveries.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Terms */}
                <div>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      {...signupForm.register('agreeTerms', { required: 'You must agree to continue' })}
                      className="mt-0.5 rounded border-input text-primary focus:ring-ring"
                    />
                    <span className="text-xs text-muted-foreground leading-relaxed">
                      I agree to MuyuRide&apos;s{' '}
                      <button type="button" className="text-primary font-500 hover:underline">Terms of Service</button>
                      {' '}and{' '}
                      <button type="button" className="text-primary font-500 hover:underline">Privacy Policy</button>.
                      I confirm all shipments comply with Kenyan law and applicable international regulations.
                    </span>
                  </label>
                  {signupForm.formState.errors.agreeTerms && <p className="mt-0.5 text-[10px] text-danger">{signupForm.formState.errors.agreeTerms.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 gradient-primary text-primary-foreground rounded-lg font-600 text-sm hover:opacity-90 transition-all duration-150 active:scale-95 disabled:opacity-60"
                >
                  {isLoading ? (
                    <><Icon name="ArrowPathIcon" size={16} className="animate-spin" /><span>Creating account…</span></>
                  ) : (
                    <><Icon name="UserPlusIcon" size={16} /><span>Create Account</span></>
                  )}
                </button>
              </form>

              {/* Legal compliance notice */}
              <div className="bg-warning/5 border border-warning/20 rounded-xl p-3">
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  <span className="font-600 text-warning">Legal Notice:</span> MuyuRide operates in compliance with Kenyan law and applicable international laws governing transportation, customs, prohibited goods, privacy, and commerce. Submitting false declarations or attempting to transport prohibited items may result in account suspension and reporting to relevant authorities.
                </p>
              </div>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <button onClick={() => setTab('login')} className="text-primary font-600 hover:underline">Sign in</button>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}